import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { forgotPasswordApi } from "../../api/authApi";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store/store";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../../components/SimpleCaptcha/SimpleCaptcha";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import "./ForgotPassword.css";

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const refreshCaptcha = useCallback(async () => {
    setCaptchaInput("");
    setCaptchaError("");
    try {
      const challenge = await getCaptchaChallengeApi();
      setCaptchaChallenge(challenge);
    } catch {
      setCaptchaChallenge(createCaptchaChallenge());
    }
  }, []);

  useEffect(() => {
    void refreshCaptcha();
  }, [refreshCaptcha]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError(t("forgotPassword.emailRequired"));
      return;
    }
    if (!captchaInput.trim()) {
      setCaptchaError(t("captcha.errorRequired"));
      return;
    }
    if (!captchaChallenge.proof) {
      setCaptchaError(t("system.serverError"));
      return;
    }
    if (captchaInput.trim().toUpperCase() !== captchaChallenge.code.toUpperCase()) {
      setCaptchaError(t("captcha.errorInvalid"));
      void refreshCaptcha();
      return;
    }
    setError("");
    setCaptchaError("");
    setSubmitting(true);
    try {
      await forgotPasswordApi({
        email: normalizedEmail,
        captcha: {
          input: captchaInput.trim(),
          code: captchaChallenge.code,
          proof: String(captchaChallenge.proof || ""),
        },
      });
      setEmail("");
      setSuccessOpen(true);
      void refreshCaptcha();
    } catch (requestError: any) {
      const isCaptchaInvalid = String(requestError?.response?.data?.code || "").trim() === "CAPTCHA_INVALID";
      if (isCaptchaInvalid) {
        setCaptchaError(t("captcha.errorInvalid"));
        void refreshCaptcha();
      } else {
        setError(t("forgotPassword.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="forgot-password-page">
      <form className="forgot-password-form" onSubmit={handleSubmit}>
        <h2>{t("forgotPassword.title")}</h2>
        <p>{t("forgotPassword.subtitle")}</p>
        <label htmlFor="forgot-email">{t("forgotPassword.emailLabel")}</label>
        <input
          id="forgot-email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError("");
          }}
          placeholder={t("forgotPassword.emailPlaceholder")}
          required
        />
        {error ? <p className="forgot-password-error">{error}</p> : null}
        <SimpleCaptcha
          idPrefix="forgot-password"
          layout="stacked"
          hideLabel
          challenge={captchaChallenge}
          value={captchaInput}
          onChange={(value) => {
            setCaptchaInput(value);
            setCaptchaError("");
          }}
          onRefresh={() => {
            void refreshCaptcha();
          }}
          label={t("captcha.label")}
          placeholder={t("captcha.placeholder")}
          refreshLabel={t("captcha.refresh")}
          error={captchaError}
        />
        <button type="submit" disabled={submitting}>
          {submitting ? t("forgotPassword.sending") : t("forgotPassword.send")}
        </button>
        <Link to="/" className="forgot-password-back">
          {t("forgotPassword.backHome")}
        </Link>
      </form>
      {successOpen ? (
        <div className="forgot-password-modal-backdrop" role="presentation">
          <div
            className="forgot-password-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-modal-title"
          >
            <h3 id="forgot-password-modal-title">{t("forgotPassword.successTitle")}</h3>
            <p>{t("forgotPassword.successMessage")}</p>
            <button
              type="button"
              className="forgot-password-modal-button"
              onClick={() => {
                setSuccessOpen(false);
                navigate("/");
              }}
            >
              {t("forgotPassword.successClose")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ForgotPassword;
